<script lang="ts">
    import { onDestroy } from 'svelte'
    import Cropper from 'cropperjs'
    // Bundled into svelte.css by esbuild. Without this the crop box, handles and
    // drag surfaces have no styling at all and the cropper is unusable.
    import 'cropperjs/dist/cropper.css'
    import Button from '@/lib/components/ui/button/button.svelte'

    export let onCrop: (blob: Blob) => void
    export let onClear: () => void
    export let maxSizeMB = 5

    type Stage = 'idle' | 'cropping' | 'confirmed'
    let stage: Stage = 'idle'

    let fileInput: HTMLInputElement
    let imgEl: HTMLImageElement
    let cropper: Cropper | null = null
    let objectUrl: string | null = null
    let previewUrl: string | null = null
    let sizeError = ''
    let zoom = 1

    // -------------------------------------------------------------------------
    // File selection
    // -------------------------------------------------------------------------
    function openPicker() {
        sizeError = ''
        fileInput.click()
    }

    function onFileSelected(e: Event) {
        const file = (e.target as HTMLInputElement).files?.[0]
        if (!file) return
        sizeError = ''

        if (file.size > maxSizeMB * 1024 * 1024) {
            sizeError = `Image must be under ${maxSizeMB} MB. (Yours is ${(file.size / 1024 / 1024).toFixed(1)} MB)`
            fileInput.value = ''
            return
        }

        revokeObjectUrl()
        objectUrl = URL.createObjectURL(file)
        // Cropper initialises via on:load on the <img> — don't call initCropper here
        stage = 'cropping'
    }

    // -------------------------------------------------------------------------
    // Cropper lifecycle — init only after the image has fully decoded
    // -------------------------------------------------------------------------
    function onImageLoad() {
        initCropper()
    }

    function initCropper() {
        destroyCropper()
        if (!imgEl) return

        cropper = new Cropper(imgEl, {
            aspectRatio: 1,
            viewMode: 1,
            dragMode: 'move',
            autoCropArea: 0.85,
            restore: false,
            guides: true,
            center: true,
            highlight: true,
            cropBoxMovable: false,
            cropBoxResizable: false,
            toggleDragModeOnDblclick: false,
            ready() {
                // Sync slider to the actual initial zoom level
                const data = (this as any).cropper.getCanvasData()
                const imgData = (this as any).cropper.getImageData()
                if (imgData.naturalWidth) {
                    zoom = Math.round((data.width / imgData.naturalWidth) * 100) / 100
                }
            },
            zoom(e: any) {
                zoom = Math.round(e.detail.ratio * 100) / 100
            },
        })
    }

    function destroyCropper() {
        cropper?.destroy()
        cropper = null
    }

    // -------------------------------------------------------------------------
    // Controls
    // -------------------------------------------------------------------------
    function onZoomSlider(e: Event) {
        const val = parseFloat((e.target as HTMLInputElement).value)
        cropper?.zoomTo(val)
        zoom = val
    }

    function rotate(deg: number) {
        cropper?.rotate(deg)
    }

    // -------------------------------------------------------------------------
    // Confirm crop → emit Blob
    // -------------------------------------------------------------------------
    function confirm() {
        if (!cropper) return
        cropper
            .getCroppedCanvas({ width: 400, height: 400, imageSmoothingQuality: 'high' })
            .toBlob((blob) => {
                if (!blob) return
                // Tear the cropper down before revoking the URL it is reading from.
                destroyCropper()
                revokeObjectUrl()
                revokePreviewUrl()
                previewUrl = URL.createObjectURL(blob)
                stage = 'confirmed'
                onCrop(blob)
            }, 'image/jpeg', 0.85)
    }

    function cancel() {
        destroyCropper()
        revokeObjectUrl()
        fileInput.value = ''
        stage = 'idle'
    }

    function changePhoto() {
        destroyCropper()
        revokeObjectUrl()
        revokePreviewUrl()
        stage = 'idle'
        onClear()
        // The file input is always mounted, so this is still inside the click's
        // user-gesture task — no setTimeout, which some browsers would block.
        openPicker()
    }

    function revokeObjectUrl() {
        if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null }
    }

    function revokePreviewUrl() {
        if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null }
    }

    onDestroy(() => {
        destroyCropper()
        revokeObjectUrl()
        revokePreviewUrl()
    })
</script>

<!-- Hidden file input -->
<input
    bind:this={fileInput}
    type="file"
    accept="image/jpeg,image/png,image/webp"
    class="hidden"
    on:change={onFileSelected}
/>

{#if stage === 'idle'}
    <button
        type="button"
        on:click={openPicker}
        class="flex flex-col items-center justify-center gap-2 w-32 h-32 rounded-full border-2 border-dashed border-muted-foreground/40 hover:border-xerteal-500 hover:bg-xerteal-50 transition-colors cursor-pointer text-muted-foreground hover:text-xerteal-600"
    >
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
        </svg>
        <span class="text-xs font-medium leading-tight text-center px-2">Upload<br/>photo</span>
    </button>
    {#if sizeError}
        <p class="text-xs text-destructive mt-1">{sizeError}</p>
    {/if}

{:else if stage === 'cropping'}
    <div class="space-y-3 w-full">
        <!--
            overflow: visible so cropperjs drag handles on the crop-box edge aren't clipped.
            height is fixed so cropperjs can calculate canvas dimensions.
        -->
        <div class="relative w-full bg-muted rounded-lg" style="height:300px; overflow:visible;">
            <img
                bind:this={imgEl}
                src={objectUrl}
                alt="Crop preview"
                on:load={onImageLoad}
                style="display:block; max-width:100%; max-height:300px;"
            />
        </div>

        <p class="text-xs text-muted-foreground text-center">
            Drag to reposition · the circle shows your avatar crop area
        </p>

        <!-- Zoom -->
        <div class="flex items-center gap-3">
            <svg class="w-4 h-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"/>
            </svg>
            <input
                type="range" min="0.1" max="3" step="0.01"
                value={zoom}
                on:input={onZoomSlider}
                class="flex-1 accent-xerteal-600 h-1.5 cursor-pointer"
                aria-label="Zoom"
            />
            <svg class="w-5 h-5 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"/>
            </svg>
        </div>

        <!-- Rotate -->
        <div class="flex items-center justify-center gap-2">
            <button type="button" on:click={() => rotate(-90)}
                class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded border hover:border-input transition-colors">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 10h10a8 8 0 018 8v2M3 10l4-4m-4 4l4 4"/>
                </svg>
                Rotate left
            </button>
            <button type="button" on:click={() => rotate(90)}
                class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded border hover:border-input transition-colors">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M21 10H11a8 8 0 00-8 8v2m18-10l-4-4m4 4l-4 4"/>
                </svg>
                Rotate right
            </button>
        </div>

        <!-- Actions -->
        <div class="flex gap-2">
            <Button class="flex-1" on:click={confirm}>Use this photo</Button>
            <Button variant="outline" on:click={cancel}>Cancel</Button>
        </div>
    </div>

{:else if stage === 'confirmed'}
    <div class="flex flex-col items-center gap-2">
        <img
            src={previewUrl}
            alt="Your avatar"
            class="w-24 h-24 rounded-full object-cover border-2 border-xerteal-400 shadow"
        />
        <button type="button" on:click={changePhoto} class="text-xs text-xerteal-600 hover:underline">
            Change photo
        </button>
    </div>
{/if}

<style>
    /* Circular preview mask inside the crop box */
    :global(.cropper-view-box),
    :global(.cropper-face) {
        border-radius: 50%;
    }
</style>
